import { useState } from 'react';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { Calendar as CalendarIcon, Download, DollarSign, ShoppingBag, CreditCard, FileBarChart, Scale, Loader2, TrendingDown } from 'lucide-react';
import { subDays, format } from 'date-fns';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import { StatsCard } from '@/components/dashboard/stats';
import { useReportCards, useReportSalesTrend, useAgingReceivables, usePopularServices } from '@/hooks/use-reports';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Suspense, lazy } from 'react';

const RevenueChart = lazy(() => import('@/components/reports/revenue-chart'));

export const Route = createFileRoute('/reports')({
  component: ReportsPage,
});

export function ReportsPage() {
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  
  const { data: reportCards, isLoading } = useReportCards(
    format(dateRange.from, 'yyyy-MM-dd'),
    format(dateRange.to, 'yyyy-MM-dd')
  );
  
  const { data: salesTrend } = useReportSalesTrend(
    format(dateRange.from, 'yyyy-MM-dd'),
    format(dateRange.to, 'yyyy-MM-dd'),
    'day'
  );
  
  const { data: agingData, isLoading: isLoadingAging } = useAgingReceivables();
  const { data: popularServices, isLoading: isLoadingPopular } = usePopularServices(
    format(dateRange.from, 'yyyy-MM-dd'),
    format(dateRange.to, 'yyyy-MM-dd')
  );

  const formatCurrency = (amount: number | string) => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(isNaN(val) ? 0 : val);
  };

  const chartData = salesTrend?.trend?.map((d: any) => ({
    date: format(new Date(d.period), 'dd/MM'),
    revenue: parseFloat(d.revenue) || 0
  })) || [];

  return (
    <ProtectedRoute>
      <MainLayout>
        <div className="flex flex-col gap-6 w-full">
          <PageHeader
            title="Sales Reports"
            actions={(
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal sm:w-[280px]'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      <span className="truncate">
                        {format(dateRange.from, 'PP')} - {format(dateRange.to, 'PP')}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="range"
                      selected={{ from: dateRange.from, to: dateRange.to }}
                      onSelect={(range) => {
                        if (range?.from && range?.to) {
                          setDateRange({ from: range.from, to: range.to });
                        } else if (range?.from) {
                          setDateRange((prev) => ({ ...prev, from: range.from! }));
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Button variant="outline" className="w-full sm:w-auto">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </>
            )}
          />

          {/* Stats Overview */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
             {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                        <CardHeader className="h-20 bg-muted/20" />
                    </Card>
                ))
             ) : (
                <>
                <StatsCard
                    title="Total Revenue"
                    value={formatCurrency(reportCards?.summary?.totalRevenue || 0)}
                    description="All time revenue"
                    icon={DollarSign}
                    className="min-h-[150px] flex flex-col justify-center"
                    href="/reports/profit-loss"
                />
                <StatsCard
                    title="Total Orders"
                    value={reportCards?.summary?.totalOrders?.toString() || "0"}
                    description="All time orders"
                    icon={ShoppingBag}
                    className="min-h-[150px] flex flex-col justify-center"
                    href="/orders"
                />
                <StatsCard
                    title="Avg. Order Value"
                    value={formatCurrency(reportCards?.summary?.avgOrderValue || 0)}
                    description="Average per order"
                    icon={CreditCard}
                    className="min-h-[150px] flex flex-col justify-center"
                    href="/reports/profit-loss"
                />
                </>
             )}
          </div>

          {/* Revenue Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Overview</CardTitle>
              <CardDescription>
                Daily revenue for the selected period.
              </CardDescription>
            </CardHeader>
            <CardContent>
               <div className="h-[350px] w-full">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    }>
                        <RevenueChart data={chartData} formatCurrency={formatCurrency} />
                    </Suspense>
                  )}
               </div>
            </CardContent>
          </Card>

          {/* Operational Reports */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Aging Receivables */}
            <Link to="/reports/aging-receivables" className="block h-full">
              <Card className="h-full transition-all duration-200 hover:shadow-md hover:border-primary/50 cursor-pointer active:scale-[0.99] transition-transform">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle>Aging Receivables</CardTitle>
                    <CardDescription>Unpaid invoices grouped by overdue age</CardDescription>
                  </div>
                  <Button variant="outline" size="sm">View Full Report</Button>
                </CardHeader>
                <CardContent>
                {isLoadingAging ? (
                  <div className="flex h-[200px] items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {[
                      { label: "0-30 Days", data: agingData?.['0_30'], color: "bg-blue-500" },
                      { label: "31-60 Days", data: agingData?.['31_60'], color: "bg-amber-500" },
                      { label: "61-90 Days", data: agingData?.['61_90'], color: "bg-orange-500" },
                      { label: "90+ Days", data: agingData?.['90_plus'], color: "bg-destructive" },
                    ].map((bucket) => {
                      const total = bucket.data?.reduce((sum: number, item: any) => sum + item.amountDue, 0) || 0;
                      const count = bucket.data?.length || 0;
                      return (
                        <div key={bucket.label} className="grid grid-cols-[100px_1fr_100px] items-center gap-4">
                          <span className="text-sm font-medium">{bucket.label}</span>
                          <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
                            <div className={cn("h-full rounded-full transition-all", bucket.color)} style={{ width: count > 0 ? '100%' : '0%' }} />
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">{formatCurrency(total)}</p>
                            <p className="text-xs text-muted-foreground">{count} invoices</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>

            {/* Popular Services */}
            <Card>
              <CardHeader>
                <CardTitle>Popular Services</CardTitle>
                <CardDescription>Top performing services by revenue</CardDescription>
              </CardHeader>
              <CardContent className="px-0">
                {isLoadingPopular ? (
                  <div className="flex h-[200px] items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead className="pl-6">Service</TableHead>
                        <TableHead className="text-right">Volume (Qty)</TableHead>
                        <TableHead className="text-right pr-6">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {popularServices?.slice(0, 5).map((service: any) => (
                        <TableRow key={service.id}>
                          <TableCell className="font-medium pl-6">
                            {service.name}
                            <div className="text-xs text-muted-foreground">{service.category}</div>
                          </TableCell>
                          <TableCell className="text-right">{service.totalVolume}</TableCell>
                          <TableCell className="text-right font-bold pr-6">{formatCurrency(service.totalRevenue)}</TableCell>
                        </TableRow>
                      ))}
                      {(!popularServices || popularServices.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                            No service data for this period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Accounting Reports */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Accounting Reports</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Link to="/reports/profit-loss">
                <Card className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50">
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <FileBarChart className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Profit & Loss</CardTitle>
                      <CardDescription>Revenue, expenses, and net profit</CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/reports/balance-sheet">
                <Card className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50">
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <Scale className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Balance Sheet</CardTitle>
                      <CardDescription>Assets, liabilities, and equity</CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/expenses">
                <Card className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50">
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <TrendingDown className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Expense Analysis</CardTitle>
                      <CardDescription>Detailed spending breakdown and charts</CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>

          {/* Outlet for sub-report routes */}
          <Outlet />
        </div>
      </MainLayout>
    </ProtectedRoute>
  );
}
